import { format, parseISO } from 'date-fns';

import { BatchCallNameField } from '@/pages/BatchCall/BatchCallCreate/components/fields/BatchCallNameField';
import { FromNumberField } from '@/pages/BatchCall/BatchCallCreate/components/fields/FromNumberField';
import { RecurringCallField } from '@/pages/BatchCall/BatchCallCreate/components/fields/RecurringField';
import { WhenToSendField } from '@/pages/BatchCall/BatchCallCreate/components/fields/WhenToSendField';

import { SubmitButton } from '@/components/submit-button';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { FormProvider } from '@/context/FormProvider';

import { useUpdateBatchCallMutation } from '@/query/scheduler.query';

import { type BatchCallUpdateFormValues, batchCallUpdateSchema } from '@/schemas/batch-call.schema';

import type { BatchCall } from '@/types/scheduler.type';

type Props = {
  call: BatchCall;
  open: boolean;
  onClose: () => void;
};

function buildInitialValues(call: BatchCall): BatchCallUpdateFormValues {
  let scheduledDate = '';
  let scheduledTime = '';

  if (call.scheduledTime) {
    try {
      const dt = parseISO(call.scheduledTime);
      scheduledDate = format(dt, 'yyyy-MM-dd');
      scheduledTime = format(dt, 'HH:mm');
    } catch {
      // leave as immediately
    }
  }

  const isRecurring = !!call.every;

  return {
    batchName: call.batchName,
    agentId: call.agent.id.toString(),
    scheduledDate,
    scheduledTime,
    scheduledTimezone: call.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    isRecurring,
    recurringFrequency: call.every ?? 'day',
    recurringDaysOfWeek: call.on ?? [],
  };
}

export const BatchCallUpdateSheet = ({ call, open, onClose }: Props) => {
  const { mutate: updateBatchCall, isPending } = useUpdateBatchCallMutation({
    onSuccess: onClose,
  });

  const handleSubmit = (values: BatchCallUpdateFormValues) => {
    updateBatchCall({ id: call.id, values });
  };

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
        <SheetHeader className="p-4 w-full relative border-b">
          <SheetTitle>Edit Batch Call</SheetTitle>
        </SheetHeader>

        <FormProvider
          initialValues={buildInitialValues(call)}
          validationSchema={batchCallUpdateSchema}
          onSubmit={handleSubmit}
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <BatchCallNameField />
            <FromNumberField />
            <WhenToSendField />
            <RecurringCallField />
          </div>

          <SheetFooter className="sticky bottom-0 bg-background border-t p-4 w-full flex flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <SubmitButton isPending={isPending} className="flex-1" />
          </SheetFooter>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};
